// TODO: Refactor this legacy
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Box, Typography, CircularProgress, ButtonGroup, Button, Grid } from '@mui/material'
import { datadogRum } from '@datadog/browser-rum'
import { useTranslation } from 'react-i18next'
import { Warning } from '@dhl/icons'
import { useWindowSize } from '@dhl/use-window'
import { useCamera, uniqueBy, useOrientationLock, useScrollLock, useFullscreenLock, useDeviceModel } from '@dhl/utils'
import type { BarcodeScannerFormat, BarcodeScannerFormatKind } from '../types'
import { findFormatKind, filterByFormatKind, isFormatKindApplicable } from '../helpers'
import { barcodeScannerFormatKinds } from '../constants/format-kinds'
import { loadPolyfills } from '../polyfills/loadPolyfills'

type Props = {
  onChange: (text: string) => void
  // reason error is unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError: (error: any) => void
  children: ReactNode
  barcodePattern: string
  isActive: boolean
  formats: BarcodeScannerFormat[]
  isOrientationLockEnabled: boolean
  parseScan: (input: string) => string
  selectedScanFormat?: BarcodeScannerFormatKind
  hasScannerPerformanceIssues: boolean
}

const ratio = 1.5

const BarcodeCameraScannerInner = ({
  onChange,
  parseScan,
  onError,
  children,
  barcodePattern,
  isActive,
  formats,
  isOrientationLockEnabled,
  selectedScanFormat,
  hasScannerPerformanceIssues,
}: Props) => {
  const { t } = useTranslation()
  const windowDimensions = useWindowSize()

  const [constraints] = useState({
    audio: false,
    video: {
      facingMode: 'environment',
      ...(hasScannerPerformanceIssues ? { height: 240, width: 320 } : {}),
    },
  })

  const [result, setResult] = useState<string | null>(null)
  const videoElementRef = useRef<HTMLVideoElement>(null)
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })
  const [isStarted, setIsStarted] = useState(false)

  const [selectedFormatKind, setSelectedFormatKind] = useState<BarcodeScannerFormatKind>(() => {
    const defaultFormatKind = findFormatKind(formats)

    if (!defaultFormatKind) {
      throw new Error(`No scan format found.`)
    }
    return selectedScanFormat && selectedScanFormat !== defaultFormatKind ? selectedScanFormat : defaultFormatKind
  })

  useOrientationLock('portrait', isOrientationLockEnabled)
  useFullscreenLock()
  useScrollLock()

  const cameraState = useCamera(constraints)

  const formatKinds = barcodeScannerFormatKinds.filter((formatKind) => isFormatKindApplicable(formats, formatKind))

  useEffect(() => {
    let isMounted = true
    const videoElement = videoElementRef.current

    if (videoElement && cameraState.state === 'available') {
      videoElement.onloadeddata = () => {
        if (isMounted) {
          setVideoDimensions({
            width: videoElement.videoWidth,
            height: videoElement.videoHeight,
          })

          setIsStarted(true)
        }
      }

      videoElement.srcObject = cameraState.mediaStream
      videoElement.play().catch((e) => {
        if (isMounted) {
          datadogRum.addError(e)
        }
      })
    }

    return () => {
      isMounted = false

      if (videoElement) {
        videoElement.srcObject = null
      }
    }
  }, [cameraState])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const videoElement = videoElementRef.current

    if (videoElement && isStarted && isActive) {
      let isMounted = true
      // The ZXing polyfill is downloaded in the background at boot, so it may not have landed yet.
      // reason: window.BarcodeDetector is untyped
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let barcodeDetector: any = null

      const renderLoop = () => {
        if (isMounted) {
          barcodeDetector
            .detect(videoElement)
            // TODO: why dont we get types from the library?
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((barcodes: any) => {
              if (isMounted) {
                // TODO: why dont we get types from the library?
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const uniqueBarcodes = uniqueBy(barcodes, (barcode: any) => barcode.rawValue)
                const isUnique = uniqueBarcodes.length === 1

                if (isUnique) {
                  const barcode = parseScan(barcodes[0].rawValue)

                  const isValid = new RegExp(barcodePattern).test(barcode)
                  const isDuplicate = barcode === result

                  if (isValid && !isDuplicate) {
                    setResult(barcode)
                  } else {
                    requestAnimationFrame(renderLoop)
                  }
                } else {
                  requestAnimationFrame(renderLoop)
                }
              }
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((error: any) => {
              // Berry: I have seen the following error: DOMException: Barcode Detection not implemented.
              // This error happened after some time of scanning, but it could not recover by just entering a new loop
              if (isMounted) {
                datadogRum.addError(error)
                onError(error)
              }
            })
        }
      }

      loadPolyfills().then(() => {
        if (!isMounted) {
          return
        }

        barcodeDetector = new window.BarcodeDetector({ formats: filterByFormatKind(formats, selectedFormatKind) })
        requestAnimationFrame(renderLoop)
      })

      return () => {
        isMounted = false
        barcodeDetector = null
      }
    }
  }, [selectedFormatKind, parseScan, barcodePattern, isStarted, isActive, result, formats, onError])

  useEffect(() => {
    let timeout: number

    if (result) {
      onChange(result)
      timeout = window.setTimeout(() => {
        setResult('')
      }, 1000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [onChange, result, setResult])

  const aspectRatio = windowDimensions.width / videoDimensions.width

  const additionalBorderWidth = Math.min(videoDimensions.width, videoDimensions.height) * 0.03

  const horizontalBorderWidth =
    aspectRatio *
    (additionalBorderWidth +
      (videoDimensions.width / videoDimensions.height > ratio
        ? (videoDimensions.width - videoDimensions.height * ratio) / 2
        : 0))

  const verticalBorderWidth =
    aspectRatio *
    (additionalBorderWidth +
      (videoDimensions.width / videoDimensions.height < ratio
        ? (videoDimensions.height - videoDimensions.width / ratio) / 2
        : 0))

  const availableHeight = Math.ceil(
    (windowDimensions.height - videoDimensions.height * aspectRatio) / 4 + verticalBorderWidth / 2
  )

  return (
    <Box
      sx={{
        background: '#202020',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'fixed',
        minHeight: '-webkit-fill-available',
        overflow: 'hidden',
        zIndex: 1300,
      }}
    >
      {(() => {
        switch (cameraState.state) {
          case 'available':
            return (
              <>
                <video
                  id="video"
                  ref={videoElementRef}
                  style={{
                    width: `${Math.ceil(videoDimensions.width * aspectRatio)}px`,
                    height: `${Math.ceil(videoDimensions.height * aspectRatio)}px`,
                    background: 'red',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                  playsInline
                  muted
                />
                {videoDimensions.width > 0 && videoDimensions.height > 0 && (
                  // Our red lines inside our video screen, so give user some guidance where to point at
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        border: '0 solid rgba(0, 0, 0, .7)',
                        boxSizing: 'border-box',
                      }}
                      style={{
                        width: `${Math.ceil(videoDimensions.width * aspectRatio)}px`,
                        height: `${Math.ceil(videoDimensions.height * aspectRatio)}px`,
                        borderTopWidth: `${Math.ceil(verticalBorderWidth)}px`,
                        borderBottomWidth: `${Math.ceil(verticalBorderWidth)}px`,
                        borderLeftWidth: `${Math.ceil(horizontalBorderWidth)}px`,
                        borderRightWidth: `${Math.ceil(horizontalBorderWidth)}px`,
                      }}
                    >
                      <Box
                        sx={{
                          borderTop: '2px solid red',
                          position: 'absolute',
                          top: '50%',
                          width: selectedFormatKind === '1D' ? '90%' : '20%',
                          left: selectedFormatKind === '1D' ? '5%' : '40%',
                        }}
                      />

                      <Box
                        sx={{
                          borderLeft: '2px solid red',
                          position: 'absolute',
                          left: '50%',
                          height: selectedFormatKind === '1D' ? '10%' : '20%',
                          top: selectedFormatKind === '1D' ? '45%' : '40%',
                        }}
                      />

                      {selectedFormatKind === 'QR' && (
                        <Box
                          sx={{
                            border: '2px solid red',
                            position: 'absolute',
                            left: '5%',
                            top: '5%',
                            width: '90%',
                            height: '90%',
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        margin: 0,
                        position: 'absolute',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'common.white',
                        textAlign: 'center',
                      }}
                      style={{
                        top: `${Math.ceil(windowDimensions.height - Math.max(50, availableHeight))}px`,
                      }}
                    >
                      <Typography variant="h4" mb={1}>
                        {(() => {
                          switch (selectedFormatKind) {
                            case 'QR':
                              return t('components.inputs.barcodeInput.scanQR')
                            case '1D':
                            default:
                              return t('components.inputs.barcodeInput.scanBarcode')
                          }
                        })()}
                      </Typography>
                      <Typography>{t('components.inputs.barcodeInput.scanExplanation')}</Typography>
                    </Box>
                    {formatKinds.length > 1 && (
                      <ButtonGroup
                        color="primary"
                        sx={{
                          top: `${Math.max(availableHeight, 75)}px`,
                          position: 'absolute',
                          transform: 'translate(0, -50%)',
                        }}
                        data-test="format-kinds"
                      >
                        {formatKinds.map((formatKind) => (
                          <Button
                            key={formatKind}
                            variant={formatKind === selectedFormatKind ? 'contained' : 'outlined'}
                            onClick={() => setSelectedFormatKind(formatKind)}
                            data-test={formatKind}
                          >
                            {formatKind}
                          </Button>
                        ))}
                      </ButtonGroup>
                    )}
                  </>
                )}
              </>
            )
          case 'unavailable': // iOS - Chrome, Sony - Chrome
          case 'denied': // other
            return (
              <Grid
                container
                wrap="nowrap"
                justifyContent="center"
                alignItems="center"
                direction="column"
                spacing={2}
                color="common.white"
              >
                <Grid>
                  <Box>
                    <Warning sx={{ fontSize: 140 }} />
                  </Box>
                </Grid>
                <Grid textAlign="center">
                  <Typography variant="h2" mb="2">
                    {t('components.inputs.barcodeInput.accessWebcam')}
                  </Typography>

                  <Box mx={2}>
                    <Typography>{t('components.inputs.barcodeInput.deniedWebcam')}</Typography>
                  </Box>
                </Grid>
              </Grid>
            )
          case 'unknown':
            return <CircularProgress />
          default:
            return null
        }
      })()}
      {children}
    </Box>
  )
}

export const BarcodeCameraScanner = (props: Omit<Props, 'hasScannerPerformanceIssues'>) => {
  const { hasScannerPerformanceIssues } = useDeviceModel()

  if (hasScannerPerformanceIssues === undefined) {
    return null
  }

  return <BarcodeCameraScannerInner {...props} hasScannerPerformanceIssues={hasScannerPerformanceIssues} />
}
